import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { StageSnapshot } from "@msc/shared";

import { resolveStageTheme } from "../../lib/customerView/brandingTheme.js";
import { StageLogos } from "./StageLogos.js";

/**
 * Der Rahmen jeder Bühnen-Folie: Logos, Kopfzeile mit Kundenname,
 * Fortschrittsbalken und Ticket-Position.
 *
 * Das Bühnen-Theme kommt aus dem Snapshot und wird als `data-stage-theme` auf
 * das Wurzelelement geschrieben — `stage.css` hängt seine Variablen genau
 * daran. Ohne Snapshot gilt „dark": Das Kundenfenster ist vom ersten Moment an
 * fertig gestaltet, es gibt keinen Zwischenzustand, den jemand sehen könnte.
 *
 * Kundendesign (D10): Bei `customer` MIT Farben im Snapshot kommen die
 * Variablen inline aus `stageVars` (`resolveStageTheme`); ohne Farben — oder
 * von einem älteren Cockpit ohne `branding` — bleibt es bei „dark". Das
 * Kundenlogo steht in jedem Theme neben dem mindsquare-Logo, sobald eines
 * hinterlegt ist.
 *
 * Port aus der Hub-App (`jira-customer-view/frontend/src/components/stage/slides.tsx`,
 * `StageFrame`); Abweichungen: keine Tailwind-Klassen, sondern die Klassen aus
 * `stage.css`, und jede Zeichenkette über `t()`.
 */
export function StageFrame({
  snapshot,
  children,
}: {
  snapshot: StageSnapshot | null;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const total = snapshot?.deck.length ?? 0;
  const pos = snapshot && snapshot.currentIndex >= 0 ? snapshot.currentIndex + 1 : 0;
  const progress = total > 0 && pos > 0 ? (pos / total) * 100 : 0;
  const customer = snapshot?.customerName ?? "";
  const { theme, style } = resolveStageTheme(snapshot);

  return (
    <div className="stage" data-testid="stage" data-stage-theme={theme} style={style}>
      <div className="stage-header">
        <div className="stage-header-left">
          <StageLogos customerLogoUrl={snapshot?.branding?.logoUrl} />
          {customer !== "" && <span>{t("stage.header", { customer })}</span>}
        </div>
        {snapshot?.mode === "ticket" && total > 0 && (
          <span>{t("stage.ticketOf", { n: pos, total })}</span>
        )}
      </div>
      <div className="stage-progress">
        <div className="stage-progress-bar" style={{ width: `${progress}%` }} />
      </div>
      {children}
    </div>
  );
}
